import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, Broom, Loader2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only "Cleanse mismatched output" action.
 *
 * Finds every row in the `output` collection whose `line_id` is not in the parent
 * SO's `assigned_lines` — these are almost always data-entry mistakes (e.g. Line 1
 * ended up on an SO that was only ever assigned to Line 5). Shows a preview per SO,
 * then on confirmation deletes the rows and re-sums each affected SO's `produced_qty`
 * so cumulative totals stay honest.
 *
 * Behaviour is idempotent — running it twice on a clean database is a no-op.
 */
export default function CleanseMismatchedOutputButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const loadPreview = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.get("/admin/output/mismatched");
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not scan for mismatched rows");
    }
    setBusy(false);
  };

  const openDialog = async () => {
    setOpen(true);
    await loadPreview();
  };

  const purge = async () => {
    if (!window.confirm(`This will DELETE ${preview?.total_rows || 0} output row(s) and re-sum produced_qty on ${preview?.summary?.length || 0} SO(s).\n\nThe rows show up in the preview list above. This cannot be undone. Continue?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/output/mismatched/purge", { confirm: true });
      setResult(data);
      toast.success(`Cleansed — ${data.deleted} row(s) deleted, ${data.affected_orders} SO(s) re-summed.`);
      onDone?.();
      await loadPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    }
    setBusy(false);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={openDialog}
        className="border-red-300 text-red-700 hover:bg-red-50"
        data-testid="cleanse-mismatched-output-btn"
      >
        <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> Cleanse mismatched output
      </Button>

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="max-w-3xl" data-testid="cleanse-mismatched-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Cleanse mismatched output
            </DialogTitle>
            <DialogDescription className="text-[12.5px]">
              Finds every output row whose <b>line_id</b> is not in the parent SO's <b>assigned_lines</b>. These are almost always data-entry mistakes — a line ended up on an SO it was never assigned to.
              <br/>Review the list below. On confirmation the rows are deleted and each affected SO's <b>produced_qty</b> is re-summed from what's left. This action is idempotent.
            </DialogDescription>
          </DialogHeader>

          {busy && !preview && (
            <div className="flex items-center gap-2 text-slate-600 text-[13px] p-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Scanning output rows…
            </div>
          )}

          {preview && (
            <div className="space-y-3">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[13px]">
                <div><b>{preview.total_rows}</b> mismatched row(s) found across <b>{preview.summary?.length || 0}</b> SO(s).</div>
                {preview.total_rows === 0 && (
                  <div className="text-emerald-700 mt-1">No mismatches. All output rows are aligned with their SO's assigned lines.</div>
                )}
              </div>

              {preview.summary?.length > 0 && (
                <div className="max-h-72 overflow-y-auto rounded-md border border-slate-200">
                  <table className="w-full text-[12.5px]">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr className="text-left text-[10.5px] uppercase tracking-widest text-slate-600">
                        <th className="py-1.5 px-2">SO</th>
                        <th className="py-1.5 px-2">Bad lines</th>
                        <th className="py-1.5 px-2 text-right">Rows</th>
                        <th className="py-1.5 px-2 text-right">Total qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.summary.map((s) => (
                        <tr key={s.order_number} className="border-b border-slate-100">
                          <td className="py-1.5 px-2 font-mono text-[#7C1D3A] font-semibold">{s.order_number}</td>
                          <td className="py-1.5 px-2">{(s.lines || []).join(", ")}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{s.count}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{s.qty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {result && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[12.5px] text-emerald-900">
                  <div className="font-semibold">Cleanse complete</div>
                  <div>Deleted <b>{result.deleted}</b> row(s). Re-summed produced_qty on <b>{result.affected_orders}</b> SO(s).</div>
                  {(result.resummed || []).slice(0, 5).map((r) => (
                    <div key={r.order_number} className="mt-1 font-mono">
                      {r.order_number} → new produced_qty: <b>{r.new_produced_qty}</b>
                    </div>
                  ))}
                  {(result.resummed || []).length > 5 && (
                    <div className="mt-1 italic">…and {result.resummed.length - 5} more.</div>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}><X className="h-4 w-4 mr-1.5" /> Close</Button>
            <Button
              onClick={purge}
              disabled={busy || !preview || preview.total_rows === 0}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="cleanse-mismatched-confirm"
            >
              {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Working…</> : <>Delete {preview?.total_rows || 0} row(s) & re-sum</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
